import { action, computed, makeObservable, observable } from "mobx";
import { LocalStorageService } from "../../utils/local-storage";
import type {
    ConnectionStatus,
    LifecycleState,
    Robot,
    RobotAvailability,
    RobotClass,
    RobotHealth,
    RobotQuickTag,
    RobotSortKey,
    RobotType,
} from "../../robots/types";
import { ROBOTS } from "../../robots/data";
import { SortDirection } from "../../api";
import type { PageInfo } from "../../api";
import { SIMULATION_TICK_INTERVAL_MS, tickRobotSimulation } from "./robot-simulation";

interface ServiceRobotsState {
    enabled: boolean;
    robots: Robot[];
}

export interface RobotsFilter {
    search?: string;
    tags?: RobotQuickTag[];
    classes?: RobotClass[];
    departmentIds?: string[];
    types?: RobotType[];
    connectivity?: ConnectionStatus[];
    health?: RobotHealth[];
    lifecycles?: LifecycleState[];
}

export interface RobotsQuery extends RobotsFilter {
    page?: number;
    pageSize: number;
    sortKey?: RobotSortKey;
    sortDirection?: SortDirection;
}

export interface RobotsList {
    robots: Robot[];
    pageInfo: PageInfo;
}

const connectivityOrder: Record<ConnectionStatus, number> = {
    online: 0,
    delayed: 1,
    offline: 2,
};

const healthOrder: Record<RobotHealth, number> = {
    healthy: 0,
    warning: 1,
    critical: 2,
    unknown: 3,
};

const availabilityOrder: Record<RobotAvailability, number> = {
    available: 0,
    busy: 1,
    charging: 2,
    paused: 3,
    blocked: 4,
    maintenance: 5,
};

const lifecycleOrder: Record<LifecycleState, number> = {
    planned: 0,
    commissioning: 1,
    active: 2,
    suspended: 3,
    maintenance: 4,
    decommissioned: 5,
};

function matchesTag(robot: Robot, tag: RobotQuickTag): boolean {
    switch (tag) {
        case "needs_attention":
            return robot.alarms > 0 || robot.health === "warning" || robot.health === "critical";
        case "close_to_discharge":
            return robot.battery != null && robot.battery <= 20 && robot.charging !== "charging";
        case "lost":
            return robot.connectivity === "offline" && robot.lastSeen != null;
    }
}

function matchesFilter(robot: Robot, filter: RobotsFilter): boolean {
    const { search, tags, classes, departmentIds, types, connectivity, health, lifecycles } =
        filter;

    if (search) {
        const needle = search.toLowerCase();
        const names = [robot.name, robot.model, robot.manufacturer];
        if (!names.some((value) => value.toLowerCase().includes(needle))) {
            return false;
        }
    }
    if (tags && tags.length > 0 && !tags.some((tag) => matchesTag(robot, tag))) {
        return false;
    }
    if (classes && classes.length > 0 && !classes.includes(robot.kind)) {
        return false;
    }
    if (
        departmentIds &&
        departmentIds.length > 0 &&
        (robot.departmentId == null || !departmentIds.includes(robot.departmentId))
    ) {
        return false;
    }
    if (types && types.length > 0 && !types.includes(robot.type)) {
        return false;
    }
    if (connectivity && connectivity.length > 0 && !connectivity.includes(robot.connectivity)) {
        return false;
    }
    if (health && health.length > 0 && !health.includes(robot.health)) {
        return false;
    }
    if (lifecycles && lifecycles.length > 0 && !lifecycles.includes(robot.lifecycle)) {
        return false;
    }
    return true;
}

function compareRobots(a: Robot, b: Robot, sortKey: RobotSortKey): number {
    switch (sortKey) {
        case "name":
            return a.name.localeCompare(b.name);
        case "connectivity":
            return connectivityOrder[a.connectivity] - connectivityOrder[b.connectivity];
        case "battery":
            return (a.battery ?? 0) - (b.battery ?? 0);
        case "alarms":
            return a.alarms - b.alarms;
        case "model":
            return a.model.localeCompare(b.model);
        case "manufacturer":
            return a.manufacturer.localeCompare(b.manufacturer);
        case "department":
            // The facade has no access to department labels, so this orders
            // by id: robots of the same department end up next to each other.
            return (a.departmentId ?? "").localeCompare(b.departmentId ?? "");
        case "lifecycle":
            return lifecycleOrder[a.lifecycle] - lifecycleOrder[b.lifecycle];
        case "lastSeen":
            return (
                (a.lastSeen ? new Date(a.lastSeen).getTime() : 0) -
                (b.lastSeen ? new Date(b.lastSeen).getTime() : 0)
            );
        case "health":
            return healthOrder[a.health] - healthOrder[b.health];
        case "availability": {
            const rank = (robot: Robot): number =>
                robot.availability != null
                    ? availabilityOrder[robot.availability]
                    : Number.MAX_SAFE_INTEGER;
            return rank(a) - rank(b);
        }
    }
}

export class ServiceRobots extends LocalStorageService<ServiceRobotsState> {
    @observable public accessor robots: Robot[] = [];

    constructor() {
        super({
            version: 1,
            defaultValue: { enabled: false, robots: ROBOTS },
            identifier: "robots",
        });
        makeObservable(this);
        this.robots = this.storedValue.robots ?? [];

        // Run simulation
        setInterval(() => this.simulationTick(), SIMULATION_TICK_INTERVAL_MS);
    }

    @action private simulationTick(): void {
        if (!this.enabled) {
            return;
        }

        this.robots = this.robots.map((robot) => {
            if (robot.integration === "simulation") {
                return { ...robot, ...tickRobotSimulation(robot, Date.now()) };
            }
            return robot;
        });
        this.saveRobots();
    }

    @computed public get enabled(): boolean {
        return this.storedValue.enabled;
    }

    @action.bound public setEnabled(enabled: boolean): void {
        this.storedValue = { ...this.storedValue, enabled };
    }

    private saveRobots(): void {
        this.storedValue = { ...this.storedValue, robots: this.robots };
    }

    @action.bound public addRobot(robot: Robot): void {
        this.robots.push(robot);
        this.saveRobots();
    }

    @action.bound public removeRobot(robotId: string): void {
        this.robots = this.robots.filter((robot) => robot.id !== robotId);
        this.saveRobots();
    }

    @action public updateRobot(robotId: string, update: Partial<Robot>): void {
        this.robots = this.robots.map((robot) =>
            robot.id === robotId ? { ...robot, ...update } : robot,
        );
        this.saveRobots();
    }

    // Facade for fetching robots with a given query.
    // Currently, this is all done in memory, without any API calls.
    public fetchRobots(query: RobotsQuery): Promise<RobotsList> {
        const { pageSize, sortKey, sortDirection = SortDirection.ASCENDING } = query;

        const robots = this.robots.filter((robot) => matchesFilter(robot, query));
        if (sortKey) {
            robots.sort(
                (a, b) =>
                    compareRobots(a, b, sortKey) *
                    (sortDirection === SortDirection.ASCENDING ? 1 : -1),
            );
        }

        const totalCount = robots.length;
        const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
        const page = Math.min(Math.max(query.page ?? 0, 0), totalPages - 1);

        return Promise.resolve({
            robots: robots.slice(page * pageSize, page * pageSize + pageSize),
            pageInfo: { totalPages, pageSize, totalCount },
        });
    }
}
